import './styles/ProductPageParts.scss';

import { Document, Page, pdfjs } from 'react-pdf';
import {
    Input,
    Loading,
    LocalizedLink,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    faChevronLeft,
    faChevronRight,
    faMinus,
    faPlus,
} from '@fortawesome/free-solid-svg-icons';

import ApplianceSingle from '@progymedia/sbi-web-mfp/src/components/appliance/ApplianceSingle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageRender from '../components/ImageRender';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import { ProductManuals } from '../components/heater-page';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import WZoom from 'vanilla-js-wheel-zoom/src/wheel-zoom';
import { debounce } from 'lodash';
import { graphql } from 'gatsby';
import { removeFirstPathname } from '../utils';
import searchIcon from '../images/icons/Loupe.svg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ProductPageParts($code: String) {
        product: productsJson(code: { eq: $code }) {
            ...cmsDataFragment
            documents {
                en_CA
                en_US
                fr_CA
            }
            schema {
                en_CA
                en_US
                fr_CA
            }
            attributes {
                marque {
                    values {
                        fr_CA
                        en_CA
                        en_US
                    }
                }
            }
            parts {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                description {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                images {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    nb_per_appliance {
                        values {
                            en_CA
                        }
                    }
                    schema_number {
                        values {
                            en_CA
                        }
                    }
                }
            }
        }
        allProductsCode: allProductsJson {
            nodes {
                code
            }
        }
    }
`;
export default function ProductPageParts({
    data,
}: {
    data: Queries.ProductPagePartsQuery;
}) {
    const { product, allProductsCode } = data;
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const [wzoom, setWzoom]: [any, any] = useState({});
    const productImage = product?.images?.[1]
        ? product?.images?.[1][currentLocale]
        : '';

    const explodedParts: Array<Queries.ProductsJsonParts> = [];
    product?.parts?.forEach((part: any) => {
        const schemaNumber =
            part?.attributes?.schema_number?.values[0][currentLocale];

        if (schemaNumber?.includes(',')) {
            schemaNumber.split(',').forEach((partNumber: number) => {
                const newPart = JSON.parse(JSON.stringify(part));

                newPart.attributes.schema_number.values[0][currentLocale] =
                    partNumber;

                explodedParts.push(newPart);
            });
        } else {
            explodedParts.push(part);
        }
    });

    const imgRef = useRef(null);
    const isMobile = useMobileDetect().isMobile();
    const [pdfScale, setPdfScale] = useState(isMobile ? 80 : 100);
    const [pageNumber] = useState(1);
    const [schemaIndex, setSchemaIndex] = useState(0);
    const schemaLength = product?.schema?.length ?? 1;
    function updateSchemaIndex(modifier: number) {
        if (
            schemaIndex + modifier >= 0 &&
            schemaIndex + modifier < schemaLength
        )
            setSchemaIndex(schemaIndex + modifier);
    }
    function updatePdfScale(modifier: number) {
        if (pdfScale + modifier > 0 && pdfScale + modifier < 200)
            setPdfScale(pdfScale + modifier);
    }

    const endsWithPdf =
        product?.schema?.[schemaIndex]?.[currentLocale]?.endsWith('.pdf');
    const hasMultipleSchema = schemaLength > 1;

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    }, []);

    const sortedSchema = () => {
        if (product?.schema != null) {
            const schema = product?.schema as Array<any>;

            return schema.sort((a, b) => {
                const numA = parseInt(a.en_CA.split('-').pop().split('.')[0]);
                const numB = parseInt(b.en_CA.split('-').pop().split('.')[0]);
                return numA - numB;
            });
        } else {
            return [];
        }
    };

    return (
        <Layout className='pp-parts'>
            <SEO
                title={t('metadata:parts-and-manuals.title', {
                    SKU: product?.code,
                })}
                description={t('metadata:parts-and-manuals.description', {
                    SKU: product?.code,
                })}
            />
            <div className='pp-parts-container'>
                <div className='pp-parts-box-product'>
                    <div className='pp-image-wrapper'>
                        <ImageRender
                            className='pp-parts-box-product-image'
                            imageSource={productImage}
                            width={730}
                        />
                    </div>
                    <div className='pp-info-container'>
                        <HeaterTitle />
                        <ProductManuals
                            product={product as Queries.ProductsJson}
                            isExpandable={false}
                            isOpenByDefault={true}
                        />
                    </div>
                </div>

                <div className='pp-parts-box-parts'>
                    <span className='pp-title-small'>
                        {t('product-page.replacement-parts')}
                    </span>
                    {product?.schema && (
                        <div className='schema-slider'>
                            {endsWithPdf && hasMultipleSchema && (
                                <span className='page-nb'>{`${
                                    schemaIndex + 1
                                } / ${product?.schema?.length}`}</span>
                            )}
                            {endsWithPdf && hasMultipleSchema && (
                                <FontAwesomeIcon
                                    className='arrow left'
                                    icon={faChevronLeft}
                                    onClick={() => updateSchemaIndex(-1)}
                                />
                            )}
                            {endsWithPdf && (
                                <div className='pdf-scale'>
                                    <FontAwesomeIcon
                                        className='scale'
                                        icon={faPlus}
                                        onClick={() => updatePdfScale(20)}
                                    />
                                    <FontAwesomeIcon
                                        className='scale'
                                        icon={faMinus}
                                        onClick={() => updatePdfScale(-20)}
                                    />
                                </div>
                            )}

                            <div
                                className={
                                    endsWithPdf
                                        ? 'part-schema pdf hide-scrollbar'
                                        : 'part-schema'
                                }
                            >
                                {endsWithPdf ? (
                                    <Document
                                        className='pdf-doc'
                                        file={`/file/${
                                            sortedSchema()[schemaIndex]?.[
                                                currentLocale
                                            ]
                                        }`}
                                        loading={<Loading />}
                                    >
                                        <Page
                                            scale={pdfScale * 0.01}
                                            pageNumber={pageNumber}
                                        />
                                    </Document>
                                ) : (
                                    <>
                                        <div className='schema-zoom'>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomDown();
                                                }}
                                            >
                                                &#8722;
                                            </button>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomUp();
                                                }}
                                            >
                                                &#43;
                                            </button>
                                        </div>
                                        <div className='img-container'>
                                            {product?.schema && (
                                                <img
                                                    alt=''
                                                    className='parts-img'
                                                    id='parts-img'
                                                    ref={imgRef}
                                                    src={`/file/${
                                                        sortedSchema()[
                                                            schemaIndex
                                                        ]?.[currentLocale]
                                                    }`}
                                                    draggable={false}
                                                    onLoad={() => {
                                                        const defaultZoom =
                                                            WZoom.create(
                                                                '#parts-img',
                                                                {
                                                                    zoomOnClick:
                                                                        false,
                                                                    maxScale: 10,
                                                                    minScale: 0.35,
                                                                    speed: 50,
                                                                }
                                                            );

                                                        defaultZoom.transform(
                                                            0,
                                                            0,
                                                            1
                                                        );

                                                        setWzoom(defaultZoom);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {endsWithPdf && hasMultipleSchema && (
                                <FontAwesomeIcon
                                    className='arrow right'
                                    icon={faChevronRight}
                                    onClick={() => updateSchemaIndex(1)}
                                />
                            )}
                        </div>
                    )}
                    <div className='pp-info-container'>
                        <span className='pp-info-container-title'>
                            {t('product-page.replacement-parts')}
                        </span>
                        <PartsListing />
                    </div>
                </div>
                <div className='pp-show-product'>
                    <LocalizedLink
                        to={
                            removeFirstPathname(
                                product?.slug?.['en_CA'] as string
                            ) ?? '/404'
                        }
                    >
                        {t('product-page.see-product')}
                    </LocalizedLink>
                </div>
            </div>
        </Layout>
    );

    function HeaterTitle() {
        return (
            <>
                <div className='pp-product-infos'>
                    <div className='pp-product-infos-top'>
                        <span className='brand'>
                            {
                                product?.attributes?.marque?.values?.[0]?.[
                                    currentLocale
                                ]
                            }
                        </span>
                        <span className='pp-sku'>{product?.code}</span>
                    </div>
                    <h1 className='pp-title'>
                        {product?.title?.[currentLocale]}
                    </h1>
                </div>
            </>
        );
    }

    function PartsListing() {
        const [searchText, setSearchText] = useState('');
        const debouncedSearch = debounce(setSearchText, 1000);

        const filteredParts = useMemo(
            () =>
                explodedParts?.filter((product: any) => {
                    const prodTitle = product?.title?.[currentLocale]
                        ?.toLowerCase()
                        ?.normalize('NFD')
                        ?.replace(/[\u0300-\u036f]/g, '');
                    const prodCode = product?.code
                        ?.toLowerCase()
                        ?.normalize('NFD')
                        ?.replace(/[\u0300-\u036f]/g, '');
                    const prodDesc = product?.description?.[currentLocale]
                        ?.toLowerCase()
                        ?.normalize('NFD')
                        ?.replace(/[\u0300-\u036f]/g, '');
                    const normalizedSearch = searchText
                        ?.toLowerCase()
                        ?.normalize('NFD')
                        ?.replace(/[\u0300-\u036f]/g, '');
                    return (
                        prodTitle?.includes(normalizedSearch) ||
                        prodCode?.includes(normalizedSearch) ||
                        prodDesc?.includes(normalizedSearch)
                    );
                }),
            [searchText]
        );

        function comparePartNumber(
            a: string | undefined | null,
            b: string | undefined | null
        ) {
            let intervalStart;
            if (a && b) {
                if (a?.includes(',')) {
                    intervalStart = a?.substring(0, 2);
                    return parseInt(intervalStart) - parseInt(b);
                }

                if (b?.includes(',')) {
                    intervalStart = b?.substring(0, 2);
                    return parseInt(a) - parseInt(intervalStart);
                }
                return parseInt(a) - parseInt(b);
            }
            return 0;
        }
        // Luiz Credidio Juillet 2023 - Nous séparons et dupliquons les parties avec plus d'une valeur schema_number
        const partsBySchemasNumbers = filteredParts.flatMap((part) => {
            const schemaNumbers =
                part?.attributes?.schema_number?.values?.[0]?.en_CA?.split(
                    ','
                ) || [];

            return schemaNumbers.map((num) => {
                return {
                    ...part,
                    attributes: {
                        ...part.attributes,
                        schema_number: {
                            ...part?.attributes?.schema_number,
                            values: [
                                {
                                    ...part?.attributes?.schema_number
                                        ?.values?.[0],
                                    en_CA: num,
                                },
                            ],
                        },
                    },
                };
            });
        });

        return (
            <div className='pp-part-search'>
                <Input
                    className='pp-search-input'
                    placeholder='Recherche'
                    image={searchIcon}
                    onChange={(value) => debouncedSearch(value)}
                />

                <div className='pp-part-wrapper'>
                    <div className='pp-part-listing hide-scrollbar'>
                        {partsBySchemasNumbers
                            ?.sort((a, b) => {
                                return comparePartNumber(
                                    a?.attributes?.schema_number?.values?.[0]?.[
                                        'en_CA'
                                    ],
                                    b?.attributes?.schema_number?.values?.[0]?.[
                                        'en_CA'
                                    ]
                                );
                            })
                            ?.map((part, index) => {
                                const suggestedProduct =
                                    allProductsCode?.nodes?.filter(
                                        (item) => item.code === part?.code
                                    )[0];
                                const completeData = {
                                    ...suggestedProduct,
                                    ...part,
                                };
                                return (
                                    <PartSingle
                                        product={completeData}
                                        key={`archives-part-${part?.code}-${index}`}
                                        showLabel
                                        showPart
                                    />
                                );
                            })}
                    </div>
                </div>
            </div>
        );
    }

    function PartSingle(props: {
        product: Queries.ProductsJsonParts;
        showLabel?: boolean;
        showPart?: boolean;
    }) {
        const { attributes } = props.product;

        return (
            <>
                <span className='pp-part-number'>
                    {attributes?.schema_number?.values?.[0]?.['en_CA']}
                </span>
                <ApplianceSingle product={props.product} style='liste' />
            </>
        );
    }
}
